import React, { useEffect, useRef } from 'react';

export interface ContextMenuItem {
    label: string;
    icon?: string;
    onClick: () => void;
    divider?: boolean;
    danger?: boolean;
}

interface ContextMenuProps {
    x: number;
    y: number;
    items: ContextMenuItem[];
    onClose: () => void;
}

export default function ContextMenu(props: ContextMenuProps) {
    const { x, y, items, onClose } = props;
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose]);

    // Adjust position to keep menu within viewport
    useEffect(() => {
        if (menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            if (rect.right > viewportWidth) {
                menuRef.current.style.left = `${x - rect.width}px`;
            }
            if (rect.bottom > viewportHeight) {
                menuRef.current.style.top = `${y - rect.height}px`;
            }
        }
    }, [x, y]);

    const handleItemClick = (item: ContextMenuItem) => {
        item.onClick();
        onClose();
    };

    return (
        <div
            ref={menuRef}
            className="context-menu"
            style={{ left: x, top: y }}
        >
            {items.map((item, index) => (
                <React.Fragment key={index}>
                    {item.divider && <div className="context-menu-divider" />}
                    <div
                        className={`context-menu-item ${item.danger ? 'danger' : ''}`}
                        onClick={() => handleItemClick(item)}
                    >
                        {item.icon && <i className={`bi ${item.icon}`}></i>}
                        <span>{item.label}</span>
                    </div>
                </React.Fragment>
            ))}
        </div>
    );
}
